import {
  APP_STORE_META,
  ASSIGNED_CONTACT_ID,
  DOCUMENT_SENDER_ID,
  ELEMENTS_LOOKUP,
  ELEMENTS_META,
  GroupFields,
  GroupFieldsOptions,
  IElement,
  IRecipient,
  RecipientEntityTypeEnum,
  extractRecipientIdAndEntityName,
  getRecipientParamsFromContact,
  useTypography,
} from '@gohighlevel/ghl-proposals-common'
import { computed, toRaw } from 'vue'

import { useElementOption } from '@/composable/elementOptions'
import { useAppStore } from '@/store/app'
import { useBuilderStore } from '@/store/builder'
import i18n from '@gohighlevel/ghl-proposals-common/src/locales'
import { useUsers } from './useUsers'

export const useRecipients = () => {
  const builderStore = useBuilderStore()
  const appStore = useAppStore()
  const { toCapitalize } = useTypography()
  const { updateDocumentOption, updateGroupOption, updateOption } = useElementOption()
  const { locationStaffs } = useUsers()
  const recipients = computed(() => appStore.document?.recipients)

  const getCurrantLoggedInUserDetails = () => {
    const contactDetails = getRecipientParamsFromContact(appStore.userData, RecipientEntityTypeEnum.USERS, false)

    return {
      ...contactDetails,
      contactName: `${contactDetails.firstName} ${contactDetails.lastName}`,
    }
  }

  const signedByOptions = computed(() => {
    let allRecipients = recipients.value
    const currentUser = getCurrantLoggedInUserDetails()
    const currentUserExist = allRecipients.find(elem => {
      return elem.id === currentUser.id && elem.entityName === RecipientEntityTypeEnum.USERS
    })
    if (!currentUserExist) {
      allRecipients = [...allRecipients, currentUser]
    }
    const recipientList =
      allRecipients.map((contact: IRecipient) => ({
        ...contact,
        label: toCapitalize(
          appStore.userId === contact.id
            ? `${contact?.contactName || ''} ${i18n.global.t('common.you')}`
            : contact?.contactName || `${contact.firstName} ${contact.lastName}`
        ).trim(),
        value: `${contact.id}_${contact.entityName}`,
      })) || []
    return recipientList
  })

  const signedByOptionsWithStaff = computed(() => {
    // Handle null/undefined recipients safely
    const baseRecipients = recipients.value || []
    const allRecipients: IRecipient[] = []

    // Create a Set for O(1) lookup performance
    const recipientKeys = new Set<string>()

    // Find and separate the two priority recipients (ASSIGNED_CONTACT_ID and DOCUMENT_SENDER_ID)
    const priorityRecipients: IRecipient[] = []
    const otherRecipients: IRecipient[] = []

    baseRecipients.forEach((recipient: IRecipient) => {
      const recipientKey = `${recipient.id}_${recipient.entityName}`
      recipientKeys.add(recipientKey)

      // Check if this is one of the priority recipients
      if (
        (recipient.id === ASSIGNED_CONTACT_ID && recipient.entityName === RecipientEntityTypeEnum.CONTACTS) ||
        (recipient.id === DOCUMENT_SENDER_ID && recipient.entityName === RecipientEntityTypeEnum.USERS)
      ) {
        priorityRecipients.push(recipient)
      } else {
        otherRecipients.push(recipient)
      }
    })

    // Sort priority recipients: ASSIGNED_CONTACT_ID first, then DOCUMENT_SENDER_ID
    priorityRecipients.sort((a, b) => {
      if (a.id === ASSIGNED_CONTACT_ID) return -1
      if (b.id === ASSIGNED_CONTACT_ID) return 1
      if (a.id === DOCUMENT_SENDER_ID) return -1
      if (b.id === DOCUMENT_SENDER_ID) return 1
      return 0
    })

    // Start with priority recipients at the top
    allRecipients.push(...priorityRecipients)

    // Add other base recipients
    allRecipients.push(...otherRecipients)

    // Add current user if not exists
    const currentUser = getCurrantLoggedInUserDetails()
    const currentUserKey = `${currentUser.id}_${currentUser.entityName}`
    if (!recipientKeys.has(currentUserKey)) {
      allRecipients.push(currentUser)
      recipientKeys.add(currentUserKey)
    }

    // Add locationStaffs one by one, checking if they already exist
    locationStaffs.value.forEach((staff: IRecipient) => {
      const staffKey = `${staff.id}_${staff.entityName}`
      if (!recipientKeys.has(staffKey)) {
        allRecipients.push(staff)
        recipientKeys.add(staffKey)
      }
    })

    const recipientList =
      allRecipients.map((contact: IRecipient) => ({
        ...contact,
        label: toCapitalize(
          appStore.userId === contact.id
            ? `${contact?.contactName || ''} ${i18n.global.t('common.you')}`
            : contact?.contactName || `${contact.firstName} ${contact.lastName}`
        ).trim(),
        value: `${contact.id}_${contact.entityName}`,
      })) || []
    return recipientList
  })

  const assignRecipientToGroup = (val: string, groupId: string) => {
    const { recipientId, entityName } = extractRecipientIdAndEntityName(val)
    if (recipientId && entityName) {
      // For card save element business user is not allowed by payment widget
      // Check if group contains SaveCardDetails elements and prevent user assignment
      const group = builderStore.groups.find(g => g.id === groupId)
      const hasSaveCardDetailsElement = group?.children.some(({ elementId }) => {
        const element = builderStore.getElementById(elementId)?.element
        return element?.type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS
      })
      if (hasSaveCardDetailsElement && entityName === RecipientEntityTypeEnum.USERS) {
        return
      }
      if (entityName === RecipientEntityTypeEnum.USERS && appStore.userId === recipientId) {
        const isDuplicateUsers = recipients.value.find((el: any) => {
          return el.id === appStore.userId && el.entityName === entityName
        })
        if (!isDuplicateUsers) {
          const contactDetails = getCurrantLoggedInUserDetails()
          const allRecipients = [...recipients.value, contactDetails]
          updateDocumentOption(APP_STORE_META.RECIPIENTS, allRecipients)
        }
      }
      updateGroupOption(ELEMENTS_META.RECIPIENT, recipientId, groupId)
      updateGroupOption(ELEMENTS_META.ENTITY_NAME, entityName, groupId)

      // update group element also.
      group?.children.forEach(({ elementId }) => {
        const element = builderStore.getElementById(elementId)?.element
        if (element) {
          // For card save element business user is not allowed by payment widget
          // Skip assignment if element is SaveCardDetails and recipient is a user
          if (element.type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS && entityName === RecipientEntityTypeEnum.USERS) {
            return
          }
          updateOption(ELEMENTS_META.RECIPIENT, recipientId, element.id, element.type)
          updateOption(ELEMENTS_META.ENTITY_NAME, entityName, element.id, element.type)
        }
      })
    }
  }

  const assignRecipientsToCheckBoxGroup = (val: string, groupId: string) => {
    const recipientData = val.split('_')
    if (recipientData.length !== 2) {
      return
    }

    const recipientId = recipientData[0]
    const entityName = recipientData[1]

    // Find group once to avoid multiple lookups
    const group = builderStore.groups.find(g => g.id === groupId)
    if (!group) {
      return
    }

    // Helper function to update all elements in the group
    const updateAllGroupElements = () => {
      group.children.forEach(({ elementId }) => {
        const element = builderStore.getElementById(elementId)?.element
        if (element) {
          updateOption(ELEMENTS_META.RECIPIENT, recipientId, element.id, element.type)
          updateOption(ELEMENTS_META.ENTITY_NAME, entityName, element.id, element.type)
        }
      })
    }

    // Early return if not a USER entity
    if (entityName !== RecipientEntityTypeEnum.USERS) {
      updateGroupOption(ELEMENTS_META.RECIPIENT, recipientId, groupId)
      updateGroupOption(ELEMENTS_META.ENTITY_NAME, entityName, groupId)
      updateAllGroupElements()
      return
    }

    // Get current recipients once to avoid stale data issues
    const currentRecipients = recipients.value || []

    // Check if recipient already exists (using Set for O(1) lookup)
    const recipientKey = `${recipientId}_${entityName}`
    const existingRecipientKeys = new Set(currentRecipients.map((r: IRecipient) => `${r.id}_${r.entityName}`))

    const recipientExists = existingRecipientKeys.has(recipientKey)
    let updatedRecipients = [...currentRecipients]

    // Handle current user
    if (appStore.userId === recipientId) {
      if (!recipientExists) {
        const contactDetails = getCurrantLoggedInUserDetails()
        updatedRecipients = [...currentRecipients, contactDetails]
        updateDocumentOption(APP_STORE_META.RECIPIENTS, updatedRecipients)
      }
    } else {
      // Handle location staff (not current user)
      const locationStaffsList = locationStaffs.value || []
      const locationStaff = locationStaffsList.find((el: IRecipient) => {
        return el.id === recipientId && el.entityName === entityName
      })

      if (locationStaff && !recipientExists) {
        updatedRecipients = [...currentRecipients, locationStaff]
        updateDocumentOption(APP_STORE_META.RECIPIENTS, updatedRecipients)
      }
    }

    // Update group options
    updateGroupOption(ELEMENTS_META.RECIPIENT, recipientId, groupId)
    updateGroupOption(ELEMENTS_META.ENTITY_NAME, entityName, groupId)

    // Update all elements in the group
    updateAllGroupElements()
  }

  const assignRecipientToElement = (val: string, element: IElement) => {
    const id = element.id
    const type = element.type
    const { recipientId, entityName } = extractRecipientIdAndEntityName(val)
    if (recipientId && entityName) {
      // For card save element business user is not allowed by payment widget
      // Prevent assigning users to SaveCardDetails elements
      if (type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS && entityName === RecipientEntityTypeEnum.USERS) {
        return
      }
      if (entityName === RecipientEntityTypeEnum.USERS && appStore.userId === recipientId) {
        const isDuplicateUsers = recipients.value.find((el: any) => {
          return el.id === appStore.userId && el.entityName === entityName
        })
        if (!isDuplicateUsers) {
          const contactDetails = getCurrantLoggedInUserDetails()
          const allRecipients = [...recipients.value, contactDetails]
          updateDocumentOption(APP_STORE_META.RECIPIENTS, allRecipients)
        }
      }
    }
    updateOption(ELEMENTS_META.RECIPIENT, recipientId, id, type)
    updateOption(ELEMENTS_META.ENTITY_NAME, entityName, id, type)
  }

  const assignRecipientToFillableElement = (val: string, element: IElement) => {
    const recipientData = val.split('_')
    if (recipientData.length !== 2) {
      return
    }

    const recipientId = recipientData[0]
    const entityName = recipientData[1]

    // Early return if not a USER entity
    if (entityName !== RecipientEntityTypeEnum.USERS) {
      updateOption(ELEMENTS_META.RECIPIENT, recipientId, element.id, element.type)
      updateOption(ELEMENTS_META.ENTITY_NAME, entityName, element.id, element.type)
      return
    }

    // Get current recipients once to avoid stale data issues
    const currentRecipients = recipients.value || []

    // Check if recipient already exists (using Set for O(1) lookup)
    const recipientKey = `${recipientId}_${entityName}`
    const existingRecipientKeys = new Set(currentRecipients.map((r: IRecipient) => `${r.id}_${r.entityName}`))

    const recipientExists = existingRecipientKeys.has(recipientKey)
    let updatedRecipients = [...currentRecipients]

    // Handle current user
    if (appStore.userId === recipientId) {
      if (!recipientExists) {
        const contactDetails = getCurrantLoggedInUserDetails()
        updatedRecipients = [...currentRecipients, contactDetails]
        updateDocumentOption(APP_STORE_META.RECIPIENTS, updatedRecipients)
      }
    } else {
      // Handle location staff (not current user)
      const locationStaffsList = locationStaffs.value || []
      const locationStaff = locationStaffsList.find((el: IRecipient) => {
        return el.id === recipientId && el.entityName === entityName
      })

      if (locationStaff && !recipientExists) {
        updatedRecipients = [...currentRecipients, locationStaff]
        updateDocumentOption(APP_STORE_META.RECIPIENTS, updatedRecipients)
      }
    }

    // Update element options
    updateOption(ELEMENTS_META.RECIPIENT, recipientId, element.id, element.type)
    updateOption(ELEMENTS_META.ENTITY_NAME, entityName, element.id, element.type)
  }

  const fetchRecipientForElementOrGroup = (
    element: IElement | GroupFields<GroupFieldsOptions>,
    recipientId?: string,
    entityName?: string
  ) => {
    const assignedRecipient = recipients.value.find((recipient: IRecipient) => {
      if (recipientId && entityName) {
        return recipient.id === recipientId && recipient.entityName === entityName
      } else {
        return (
          recipient.id === element?.component?.options[ELEMENTS_META.RECIPIENT] &&
          recipient.entityName === element?.component?.options[ELEMENTS_META.ENTITY_NAME]
        )
      }
    })
    return assignedRecipient
  }

  const getSelectValue = (element: IElement | GroupFields<GroupFieldsOptions>) => {
    const recipientsList = toRaw(recipients.value)
    const lastSelectedId = element?.component.options[ELEMENTS_META.RECIPIENT]

    const userStillExistsInRecipients = recipientsList.find((el: any) => {
      return el.id === lastSelectedId
    })

    if (lastSelectedId && !userStillExistsInRecipients) {
      return null
    }

    if (element?.component?.options[ELEMENTS_META.RECIPIENT] && element?.component?.options[ELEMENTS_META.ENTITY_NAME]) {
      return `${element?.component?.options[ELEMENTS_META.RECIPIENT]}_${element?.component?.options[ELEMENTS_META.ENTITY_NAME]}`
    }
    return null
  }

  return {
    recipients,
    signedByOptions,
    signedByOptionsWithStaff,
    getCurrantLoggedInUserDetails,
    assignRecipientToGroup,
    assignRecipientsToCheckBoxGroup,
    fetchRecipientForElementOrGroup,
    assignRecipientToElement,
    assignRecipientToFillableElement,
    getSelectValue,
  }
}
